import React, {memo, useEffect} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import FormikTextField from '../../../../../components/inputs/FormikTextField';
import FormikCheckbox from '../../../../../components/inputs/FormikCheckbox';
import FormikListBox from '../../../../../components/inputs/FormikListBox';
import Panel from '../../../../../components/Panel';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions, layoutSelectors} from '../../../redux';
import {
   AuditReportParameters,
   IAuditReportParameters,
} from 'types/diplomat/reports/AuditReportParameters';
import FormikRadioButton from '../../../../../components/inputs/FormikRadioButton';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {Box, Grid} from '@mui/material';
import {GridCentered} from 'components/GridCentered';

import {formatDateTime} from 'common/dateUtils';
import {settingsSelectors} from 'redux/settings';
import {useFetchAuditSettings} from 'redux/settings/hooks/useFetchAuditSettings';
import {enums} from 'types/enums';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import FormikSwitchValuable from 'components/inputs/FormikSwitchValueable';
import {ByDateReportTypeFormPart} from 'features/layout/DialogRoutes/dialogs/reports/parts/ByDateReportTypeFormPart';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';

type Props = {
   onClose: () => void;
   values?: IAuditReportParameters;
   onSubmit: (
      values: IAuditReportParameters,
      bag: FormOnSubmitHelpers<IAuditReportParameters>
   ) => void;
   formatChoosable?: boolean;
};

const Form = () => {
   const {t} = useTranslation();

   const getValue = useFieldValueGetter<IAuditReportParameters>();
   const enabled = !getValue('allRecords');
   const byTransactionName = getValue('byTransactionName');
   const filenameContaining = getValue('filenameContaining');
   const containing = getValue('containing');

   const tNames = useSelector(layoutSelectors.transactionNames);
   const transactions = React.useMemo(() => ['<All Inbound>', '<All Outbound>', ...tNames], [
      tNames,
   ]);
   const archiveUsed = useSelector(layoutSelectors.archiveUsed);
   const archiveDateRange = useSelector(layoutSelectors.archiveDateRange);

   return (
      <Panel>
         <Grid container spacing={3}>
            <Grid item sm={12}>
               <FormikRadioButton
                  name="allRecords"
                  key="allRecordsTrue"
                  value={true}
                  label={t('Select All Records')}
               />
            </Grid>
         </Grid>
         <Grid container spacing={3}>
            <Grid item sm={12}>
               <FormikRadioButton
                  name="allRecords"
                  key="allRecordsFalse"
                  value={false}
                  label={t('Select Records')}
               />
            </Grid>
         </Grid>
         <Box marginLeft={8}>
            <Grid container spacing={3}>
               <Grid item sm={3}>
                  <FormikCheckbox
                     name="byTransactionName"
                     label={t('By Transaction Name:')}
                     disabled={!enabled}
                  />
               </Grid>
               <Grid item sm={8}>
                  <FormikListBox
                     name="transactionNames"
                     options={transactions}
                     disabled={!enabled || !byTransactionName}
                     // height="230px"
                  />
               </Grid>
            </Grid>
            <Grid container spacing={3}>
               <ByDateReportTypeFormPart />
            </Grid>
            <Grid container spacing={3}>
               <Grid item sm={3}>
                  <FormikCheckbox
                     name="filenameContaining"
                     label={t('By filename containing:')}
                     disabled={!enabled}
                  />
               </Grid>
               <Grid item xs={6} sm={8}>
                  <FormikTextField
                     name="filenameContainingText"
                     placeholder=""
                     disabled={!enabled || !filenameContaining}
                  />
               </Grid>
            </Grid>
            <Grid container spacing={3}>
               <Grid item sm={3}>
                  <FormikCheckbox name="containing" label={t('Containing:')} disabled={!enabled} />
               </Grid>

               <Grid item xs={6} sm={8}>
                  <FormikTextField
                     name="containingText"
                     placeholder=""
                     disabled={!enabled || !containing}
                  />
               </Grid>
            </Grid>
            {archiveUsed &&
            archiveDateRange.oldestArchiveDate &&
            archiveDateRange.youngestArchiveDate ? (
               <Grid container spacing={3}>
                  <Grid item sm={3}>
                     <FormikCheckbox
                        name="includeArchive"
                        label={t('Include Archive Records')}
                        disabled={!enabled}
                     />
                  </Grid>
                  <GridCentered item sm={4}>
                     <div>
                        <Trans>From</Trans> {formatDateTime(archiveDateRange.oldestArchiveDate)}
                     </div>
                  </GridCentered>
                  <GridCentered item sm={4}>
                     <div>
                        <Trans>To</Trans> {formatDateTime(archiveDateRange.youngestArchiveDate)}
                     </div>
                  </GridCentered>
               </Grid>
            ) : null}
         </Box>
      </Panel>
   );
};

const AuditReportForm: ReactFChildrenProps<Props> = memo(
   ({values, onClose, onSubmit, formatChoosable = true}) => {
      const params = values || {
         allRecords: false,
         byDate: true,
         relativeDate: true,
         relativeDateType: enums.TimeRangeReportType.enum.THIS_MONTH,
      }; // useSelector(layoutSelectors.auditReportParams);

      const auditSettings = useSelector(settingsSelectors.auditTrailSettings);
      useFetchAuditSettings();

      const dispatch = useDispatch();

      const isArchive =
         auditSettings.auditRequired &&
         auditSettings.archive &&
         auditSettings.dbSettings.dbType === enums.dbType.enum.SQL;

      useEffect(() => {
         dispatch(layoutActions.getArchiveUsed);
         if (auditSettings.archive) {
            dispatch(layoutActions.getArchiveDateRange);
         }
      }, [dispatch, isArchive, auditSettings.archive]);

      return (
         <Formik
            initialValues={AuditReportParameters.cast(params) as IAuditReportParameters}
            validationSchema={AuditReportParameters}
            onSubmit={onSubmit}
            initialTouched={['allRecords']}
         >
            <>
               <Form />

               {formatChoosable && (
                  <Box
                     sx={{
                        margin: '0 auto',
                        width: '18rem',
                     }}
                  >
                     <FormikSwitchValuable
                        name="_format"
                        options={[enums.reportFormats.options[0], enums.reportFormats.options[1]]}
                     />
                  </Box>
               )}
               <FormFooterButtons cancel={onClose} />
            </>
         </Formik>
      );
   }
);

export default AuditReportForm;
